import { HandBack, ecId, ecLean, inventoryId, record, upper, type Row } from '../ability/context.js';
import { readItemField } from '../compile/item-field-reader.js';
import { encounterField } from '../../shared/array-types.js';
import { statusItemId, statusRemovalBefore } from '../statuses/expiry.js';
import { ftTrapArmedStatusRow } from '../port/k4-dispatch/ft_trap_fire.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { get, type ReadEnv } from '../port/k4-dispatch/shared.js';
import { mountedTrapAbilityIds } from './mounted.js';
import { resolveTrapCheck, trapSkillFor, DISCERN_TRAP, type TrapCheckInput, type TrapCheckSave } from './skill-check.js';
import type { RollSource } from '../actions/roll-source.js';

export interface DiscernPlan {
  success: boolean; total: number; dc: number; skill_id: string;
  verdict: TrapCheckSave;
  noticed: { kind: 'noticed'; status_id: string; before: Row; private_before: number } | null;
  chargeBefore: Array<{ holder: string; item_id: string; charge_id: string | null; charges: number | null }>;
}

/** The raw envelope is held beside the compiled surface. An empty saved list proves absence. */
export function discernChargeRows(actor: Row, encounter: Row): Row[] | null {
  for (const owner of [actor, encounter]) {
    const base = record(record(owner._entity_base)?.base);
    if (Array.isArray(base?.charges)) return base.charges as Row[];
    const members = base?.encounter_creatures_arr;
    if (!Array.isArray(members)) continue;
    const member = members.map(record).find(row => upper(readItemField(row, 'entity_id')) === ecId(actor));
    if (Array.isArray(member?.charges)) return member.charges as Row[];
  }
  return null;
}

/** Admission, routing, AP and all aftermath are settled before the live check draws. */
export function resolveDiscern(input: Omit<TrapCheckInput, 'skillId' | 'itemIdUsed' | 'dc'> & { chargeRows: Row[] | null }, roll: RollSource):
  { ok: true; plan: DiscernPlan } | { ok: false; error: string } {
  const { read, host } = input, aid = upper(input.actorId), actor = input.members.get(aid);
  if (!actor) return { ok: false, error: 'trap_check:actor-not-held' };
  const armed = ftTrapArmedStatusRow(host, browserDependencies({ creature_statuses: () => read.statuses(host),
    item_field: (row: unknown) => statusItemId(row as Row) }) as unknown as ReadEnv);
  if (!armed) return { ok: false, error: 'not_armed' };
  const armedId = upper(get(armed, 'status_id'));
  const status = read.statuses(host).find(row => upper(read.value(row, 'status_id')) === armedId)!;
  const mounted = mountedTrapAbilityIds(host, read.entries), payload = mounted[0] ? read.definition(mounted[0]) : null;
  const magical = payload && String(read.value(payload, 'trap_classification') ?? '').trim().toLowerCase() === 'magical';
  const skill_id = trapSkillFor(magical ? 'magical' : 'mechanical');
  const rawDc = read.inventoryField?.(host, 'inventory_inspection_dc');
  const dc = rawDc != null && rawDc !== '' && Number.isFinite(Number(rawDc)) ? Math.trunc(Number(rawDc)) : 10;
  const aps = Number(ecLean(actor, 'aps') ?? 0), cost = Number(encounterField(input.encounter, 'in_combat') ?? 0) === 1 ? 3 : 0;
  if (aps < cost) return { ok: false, error: 'not_enough_aps' };
  // Pin every potential CMRT pool before rolling. The compiled current can be a default;
  // the separate raw before row proves whether SQL actually contains a pool.
  const chargeBefore: DiscernPlan['chargeBefore'] = [];
  if (input.chargeRows) for (const row of input.chargeRows) {
    if (upper(read.value(row, 'charge_holder')) !== aid || Number(read.value(row, 'charge_deleted') ?? 0) !== 0) continue;
    chargeBefore.push({ holder: aid, item_id: upper(read.value(row, 'item_id')), charge_id: upper(read.value(row, 'charge_id')),
      charges: Number(read.value(row, 'charges') ?? 0) });
  }
  const result = resolveTrapCheck({ ...input, nativeSave: true, skillId: skill_id, itemIdUsed: DISCERN_TRAP, dc,
    savePreflight: save => {
      if (save.charges.length && !input.chargeRows) throw new HandBack('trap_check:raw-charges-not-held');
      for (const change of save.charges) {
        const before = chargeBefore.find(row => row.holder === change.holder && row.item_id === change.item_id);
        if (before && before.charges !== change.before) throw new HandBack('trap_check:charge-snapshot-moved');
      }
    } }, roll);
  if (result.kind !== 'rolled') return { ok: false, error: result.reason };
  const verdict = result.save!;
  // This is an absolute actor save, including an unchanged OOC AP value for CAS.
  (verdict.ec[aid] ??= {}).aps = aps - cost;
  (verdict.ecBefore[aid] ??= {}).aps = aps;
  const pools = new Map(verdict.charges.map(change => [change.holder + '|' + change.item_id, change]));
  if (result.success) verdict.rows.push({ roll_type_1: 'trap_discerned', roll_type_2: null, roll_result: 0,
    roll_item_id: inventoryId(host), roll_by: aid, roll_to: inventoryId(host) });
  return { ok: true, plan: { success: result.success, total: result.total, dc, skill_id, verdict,
    chargeBefore: [...pools.values()].map(change => chargeBefore.find(row => row.holder === change.holder && row.item_id === change.item_id)
      ?? { holder: change.holder, item_id: change.item_id, charge_id: null, charges: null }),
    noticed: result.success ? { kind: 'noticed', status_id: armedId, before: statusRemovalBefore(status, inventoryId(host)),
      private_before: Number(read.value(status, 'status_private') ?? 0) } : null } };
}
